import { createProduct, updateProduct } from "../api/productApi";
import {
  BeerFormState,
  ProductImage,
  ProductData,
  SanityData,
  BeerFormStateEdit,
  BackendImage,
  ProductToGeneratePresignedUrl,
} from "../constants/types";
import { productEndPoints } from "../constants/endpoints";
import { getPresignedUrls, uploadFileToS3Api } from "../api/customerAPI";
import bucketMapping from "../constants/S3BuketMapping";
import { uploadSingleFileWithPresignedUrl } from "../utils/files";

export const createProductService = async (data: BeerFormState) => {
  try {
    const productData = extractProductData(data);
    const sanityData = getSanityData(data);

    const updatedProductImagesToUpload = await convertFilesToProductImages(
      data.fileUpload
    );
    const imagesForPresignedUrl = await convertFilesToGeneratePresignedUrl(
      data.fileUpload
    );

    const presignedUrls = await getPresignedUrls(
      imagesForPresignedUrl,
      bucketMapping.product
    );

    const finalUploadedDoc: any[] = await uploadMultipleFilesWithPresignedUrls(
      presignedUrls as {
        presignedUrl: string;
        key: string;
        fileName: string;
      }[],
      updatedProductImagesToUpload
    );

    const productImages = finalUploadedDoc.map((uploadedFile) => ({
      url: uploadedFile.documentId?.split("?")[0],
      fileName: uploadedFile.fileName,
    }));

    const response = await createProduct(
      {
        productData,
        productImages,
        sanityData,
      },
      productEndPoints.createNewProduct
    );
    return response;
  } catch (error: any) {
    console.log("error", error);
    throw error;
  }
};

export const extractProductData = (
  input: BeerFormState | BeerFormStateEdit
): ProductData => {
  const priceInCents = parseFloat(input.price) * 100;
  const discountedPriceInCents = 0;
  const disabled = false;
  const productData = {
    priceInCents,
    discountedPriceInCents,
    disabled,
    ibu: Number(input.ibu),
    abv: Number(input.abv),
    preorder: !!input.preorder,
    displayInHome: !!input.preorder,
    quantity_20L: Number(input.quantity_20L ?? 0),
    quantity_30L: Number(input.quantity_30L ?? 0),
    quantity_50L: Number(input.quantity_50L ?? 0),
  };
  return productData;
};

export const convertFilesToProductImages = async (
  files: File[]
): Promise<ProductImage[]> => {
  const productImages: ProductImage[] = await Promise.all(
    files.map(async (file) => {
      return {
        fileName: file.name,
        fileContent: file,
        fileType: file.type,
      };
    })
  );
  return productImages;
};

export const convertFilesToGeneratePresignedUrl = async (
  files: File[]
): Promise<ProductToGeneratePresignedUrl[]> => {
  const productImages: ProductToGeneratePresignedUrl[] = await Promise.all(
    files.map(async (file) => {
      return {
        fileName: file.name,
        fileType: file.type,
      };
    })
  );
  return productImages;
};

export const getSanityData = (data: {
  beerName: string;
  description: string;
  ed: number;
  basePrice: number;
}): SanityData => {
  return {
    name: data.beerName,
    description: data.description,
    primaryButtonTitle: "Buy Now",
    secondaryButtonTitle: "Learn More",
    ED: Number(data.ed ?? 0),
    basePrice: Number(data.basePrice ?? 0),
  };
};

export const mapBackendToFormState = (
  backendResponse: any
): BeerFormStateEdit => {
  return {
    beerName: backendResponse.name,
    quantity: "0", // Populate as needed
    description: backendResponse.description,
    basePrice: backendResponse.basePrice,
    ed: backendResponse.ed,
    price: (backendResponse.priceInCents / 100).toString(),
    fileUpload: [], // No files uploaded initially
    existingImages: backendResponse.images.map((image: BackendImage) => ({
      url: image.url,
      fileName: image.fileName,
    })),
    ibu: backendResponse.ibu,
    abv: backendResponse.abv,
    preorder: Boolean(
      backendResponse.isPreorder ?? backendResponse.preorder ?? false
    ),
    quantity_20L: Number(backendResponse.quantity_20L ?? 0),
    quantity_30L: Number(backendResponse.quantity_30L ?? 0),
    quantity_50L: Number(backendResponse.quantity_50L ?? 0),
  };
};

export const updateProductService = async (
  data: BeerFormStateEdit,
  id: string
) => {
  try {
    const productData = extractProductData(data);

    const productImages = data.existingImages;
    const sanityData = getSanityData(data);

    const updatedProductImagesToUpload = await convertFilesToProductImages(
      data.fileUpload
    );

    const imagesForPresignedUrl = await convertFilesToGeneratePresignedUrl(
      data.fileUpload
    );

    const presignedUrls = await getPresignedUrls(
      imagesForPresignedUrl,
      bucketMapping.product
    );

    const finalUploadedDoc: any[] = await uploadMultipleFilesWithPresignedUrls(
      presignedUrls as {
        presignedUrl: string;
        key: string;
        fileName: string;
      }[],
      updatedProductImagesToUpload
    );

    const updatedProductImages = finalUploadedDoc?.map((uploadedFile) => ({
      url: uploadedFile.documentId?.split("?")[0],
      fileName: uploadedFile.fileName,
    }));

    const response = await updateProduct(
      {
        productData,
        updatedProductImages,
        productImages,
        sanityData,
      },
      productEndPoints.createNewProduct,
      id
    );

    return response;
  } catch (error: any) {
    console.log("error", error);
    throw error;
  }
};

export const uploadMultipleFilesWithPresignedUrls = async (
  presignedUrls: { presignedUrl: string; key: string; fileName: string }[],
  documents: ({
    fileContent: string | File | Blob;
    fileType: string;
    fileName: string;
  } | null)[]
) => {
  // Filter out null documents before processing
  const validDocuments = documents.filter(
    (
      doc
    ): doc is {
      fileContent: File | Blob;
      fileType: string;
      fileName: string;
    } => doc !== null && doc.fileName.trim() !== ""
  );

  const uploadedDocuments = await Promise.all(
    presignedUrls.map(async (presignedFile, index) => {
      const document = validDocuments[index]; // Ensure correct mapping

      if (!document) {
        console.warn(
          `Skipping upload: No document found for ${presignedFile.fileName}`
        );
        return null;
      }

      const isUploaded = await uploadSingleFileWithPresignedUrl(
        presignedFile.presignedUrl,
        document.fileContent,
        document.fileType
      );

      if (isUploaded) {
        return {
          documentId: presignedFile.presignedUrl, // S3 URL
          fileName: document.fileName,
          key: presignedFile.key, // S3 key
        };
      } else {
        throw new Error(`Upload failed for ${document.fileName}`);
      }
    })
  );

  // Remove null values before returning
  return uploadedDocuments.filter((doc) => doc !== null);
};
